import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from '../hooks/useSettings';

const ContactPage = () => {
  const { settings } = useSettings();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    adventureType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const adventureTypes = [
    { value: '', label: 'Select Adventure Type' },
    { value: 'dive-exhibition', label: 'Dive Exhibition (Beginner)' },
    { value: 'guided-shore-dive', label: 'Guided Shore Dive' },
    { value: 'dive-trip-shore', label: 'Full Day Dive Trip' },
    { value: 'night-dive-experience', label: 'Night Dive Experience' },
    { value: 'scooter-dive-adventure', label: 'Scooter Dive Adventure' },
    { value: 'underwater-treasure-hunt', label: 'Underwater Treasure Hunt' },
    { value: 'camping-diving-1night', label: 'Weekend Dive & Camp' },
    { value: 'camping-diving-2day', label: 'Extended Dive & Camp' },
    { value: 'camping-only-2day', label: 'Land-based Weekend' },
    { value: 'custom', label: 'Custom Adventure Package' },
    { value: 'general', label: 'General Inquiry' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          adventure_type: formData.adventureType,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          adventureType: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-slate-50 to-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-ocean-900 via-ocean-800 to-ocean-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Get In Touch</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
              Ready to start your adventure? Have questions about our experiences? 
              Ahmed is here to help you plan the perfect nature reconnection journey.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-lg border border-slate-200 h-fit lg:sticky lg:top-24"
            >
              <h3 className="text-2xl font-bold text-slate-800 mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <span className="text-coral-500 text-2xl mr-4 mt-1">📧</span>
                  <div>
                    <div className="font-semibold text-slate-800">Email</div>
                    <a href={`mailto:${settings.contact_email}`} className="text-coral-600 hover:text-coral-700">
                      {settings.contact_email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-coral-500 text-2xl mr-4 mt-1">📱</span>
                  <div>
                    <div className="font-semibold text-slate-800">WhatsApp</div>
                    <a href={`https://wa.me/${settings.whatsapp_number?.replace(/[\s+]/g, '')}`} className="text-coral-600 hover:text-coral-700">
                      {settings.whatsapp_number}
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-coral-500 text-2xl mr-4 mt-1">📍</span>
                  <div>
                    <div className="font-semibold text-slate-800">Location</div>
                    <div className="text-slate-600">
                      {settings.location}
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <span className="text-coral-500 text-2xl mr-4 mt-1">🕒</span>
                  <div>
                    <div className="font-semibold text-slate-800">Operating Hours</div>
                    <div className="text-slate-600">
                      {settings.business_hours}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-4">Follow Our Adventures</h4>
                <div className="flex space-x-4">
                  {[
                    { platform: 'Instagram', url: 'https://instagram.com/divecampadventures', icon: '📷' },
                    { platform: 'Facebook', url: 'https://facebook.com/divecampadventures', icon: '📘' },
                    { platform: 'YouTube', url: 'https://youtube.com/@divecampadventures', icon: '📺' }
                  ].map((social) => (
                    <a
                      key={social.platform}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-2xl hover:scale-110 transition-transform"
                      title={social.platform}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-lg border border-slate-200"
            >
              <h3 className="text-2xl font-bold text-slate-800 mb-6">Send a Message</h3>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-xl border border-green-200">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">✅</span>
                    <div>
                      <div className="font-semibold">Message Sent Successfully!</div>
                      <div>Ahmed will get back to you within 24 hours.</div>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-xl border border-red-200">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">❌</span>
                    <div>
                      <div className="font-semibold">Error Sending Message</div>
                      <div>Please try again or contact us directly via WhatsApp.</div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="First Name *"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last Name *"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="WhatsApp Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
                  />
                </div>

                <select
                  value={formData.adventureType}
                  onChange={(e) => setFormData({...formData, adventureType: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all"
                  required
                >
                  {adventureTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>

                <textarea
                  placeholder="Tell me about your goals for this experience, any questions you have, or special requirements... *"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows="6"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-coral-500 focus:ring-2 focus:ring-coral-200 transition-all resize-none"
                  required
                ></textarea>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-coral-500 to-coral-600 hover:from-coral-600 hover:to-coral-700 disabled:from-slate-400 disabled:to-slate-500 text-white py-4 rounded-xl font-semibold transition-all transform hover:scale-105 shadow-lg"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Sending Message...
                    </div>
                  ) : (
                    'Send Message to Ahmed'
                  )}
                </button>
              </form>

              <div className="mt-6 text-sm text-slate-500 text-center">
                * Required fields. Your information is kept private and secure.
              </div>
            </motion.div>
          </div>
        </div>

        {/* FAQ Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-20 bg-gradient-to-r from-slate-100 to-slate-200 p-8 rounded-3xl"
        >
          <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">Quick Response Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">🚀 Fastest Response</h4>
              <p className="text-slate-600 text-sm">WhatsApp messages typically answered within 2-4 hours during operating hours</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">📧 Email Response</h4>
              <p className="text-slate-600 text-sm">Email inquiries answered within 24 hours, often much sooner</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">🗓️ Booking Timeline</h4>
              <p className="text-slate-600 text-sm">Book at least 3-7 days in advance for best availability</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">🌊 Weather Dependent</h4>
              <p className="text-slate-600 text-sm">All water activities subject to weather conditions - safety first!</p>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default ContactPage;